"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Building2, Truck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * CAMBIOS APLICADOS:
 * - Reordenado para que:
 * 1) Freightliner Cascadia 125 (Grúa) quede primero
 * 2) International 9200 quede segundo
 * 3) Volvo FH500 (VENDIDO) quede tercero
 * 4) Mack Anthem (VENDIDO) quede cuarto
 * - Eliminado Volvo VM330
 */

const machines = [{
  id: 18,
  name: "Freightliner Cascadia 125/Holmes 750",
  category: "Grúa / Rescate",
  images: ["https://promaquinas.cl/wp-content/uploads/2026/05/Freightliner-Cascadia-3-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3120.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/Freightliner-Cascadia-1-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/05/Freightliner-Cascadia-2-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3129.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3134.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3138.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/Diseno-sin-titulo-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/05/Diseno-sin-titulo-2-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3155.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3157.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3153.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3154.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3163.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/05/IMG_3181-scaled.jpg", "https://promaquinas.cl/wp-content/uploads/2026/05/Freightliner-Cascadia-4-scaled.png"],
  feature: "$86 900 000 CLP *Valor más IVA",
  icon: Truck,
  description: "Tractocamión aerodinámico, imponente y altamente confiable, diseñado para dominar el rescate pesado. Equipado con la legendaria grúa de doble pluma Holmes 750.",
  year: "2015",
  price: "$86 900 000 CLP +IVA",
  specs: {
    type: "Grúa Rescate Pesado",
    brand: "Freightliner Cascadia 125",
    mileage: "347.865 km",
    engine: "motor Detroit DD15 de 6 cilindros en línea turbodiésel, 14,8 L, con aproximadamente 500 HP y 2.500 Nm de torque",
    traction: "8x4 (3 Ejes Traseros)",
    tech: "Computador de a bordo y máximo confort",
    application: "Rescate pesado, asistencias exigentes y recuperación de alto tonelaje.",
    extras: "Grúa Holmes 750 (3 huinches de 100T Aprox), Levantadora ZZ Zacklift, Cama (Litera)"
  }
}, {
  id: 3,
  name: "International 9200",
  category: "Tractocamión",
  images: ["https://promaquinas.cl/wp-content/uploads/2026/01/Diseno-sin-titulo-11-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0953.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/Convertida-3-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/Convertida-4-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/01/Convertida-2-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/01/Diseno-sin-titulo-3-4-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0947.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/Convertida-4-1-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0966.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0959.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0970.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0968.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0954.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0948.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0950.JPG-scaled.jpeg", "https://promaquinas.cl/wp-content/uploads/2026/01/Convertida-3-1-scaled.png", "https://promaquinas.cl/wp-content/uploads/2026/01/IMG_0946.JPG-scaled.jpeg"],
  feature: "$30 900 000 CLP *Valor más IVA",
  icon: Truck,
  description: "Tractocamión robusto y confiable, diseñado para trabajo pesado y transporte de larga distancia, con un alto estándar de durabilidad, ideal para operaciones exigentes y continuidad operacional en ruta.",
  year: "2016",
  price: "$30 900 000 CLP +IVA",
  specs: {
    type: "Tractocamión",
    brand: "International 9200",
    mileage: "706.985 km",
    engine: "Cummins ISM, 6 cilindros en línea, potencia entre 400 y 450 HP, torque hasta 2100 Nm",
    transmission: "Manual Eaton Fuller, 18 velocidades",
    traction: "6x4",
    suspension: "Neumática",
    brakes: "Frenos de aire con ABS",
    tech: "Instrumentación analógica con computador de a bordo",
    application: "Transporte de carga general, tolvas y contenedores",
    extras: "Cama"
  }
}, {
  id: 1,
  name: "Volvo FH500",
  category: "Tractocamión",
  images: ["https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0700-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo2-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-1-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0709-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0707-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0695-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0690-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0687-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0686-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0727-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0705-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-3-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0703-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0713-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-4-scaled.png"],
  feature: "VENDIDO",
  icon: Truck,
  description: "Tractocamión de alto rendimiento diseñado para transporte de larga distancia con máxima eficiencia y confort.",
  year: "2019",
  price: "VENDIDO",
  specs: {
    type: "Tractocamión",
    brand: "Volvo FH500",
    mileage: "626.200",
    engine: "Volvo D13, 12.8 litros, 6 cilindros en línea, 500 CV y 2.500 Nm",
    transmission: "Automatizada I-Shift (doble embrague), de 12 velocidades",
    traction: "6x4",
    suspension: "Neumática",
    brakes: "EBS, ABS y control de tracción",
    tech: "Pantallas digitales y conectividad integrada",
    application: "Transporte de larga distancia",
    extras: "Cama, Cooler"
  }
}, {
  id: 4,
  name: "Mack Anthem",
  category: "Tractocamión",
  images: ["https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-4-1-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-3-1-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-7-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-6-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-2-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-1-1-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0636-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0639-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0642-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0671-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0724-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0675-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0665-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0669-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0668-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0631-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/IMG_0657-scaled.jpg", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-5-scaled.png", "https://hopeful-ram-2.10web.cloud/wp-content/uploads/2026/01/Diseno-sin-titulo-8-scaled.png"],
  feature: "VENDIDO",
  icon: Truck,
  description: "Tractocamión robusto y confiable, diseñado para transporte de larga distancia con alto estándar de durabilidad.",
  year: "2020",
  price: "VENDIDO",
  specs: {
    type: "Tractocamión",
    brand: "Mack Anthem",
    mileage: "547.000 km",
    engine: "Mack MP8, 12.8 litros, 6 cilindros en línea, 440 CV y 2.300 Nm",
    transmission: "Automatizada Mack mDRIVE, 12 velocidades",
    traction: "6x4",
    suspension: "Neumática",
    brakes: "ABS, EBS y control de tracción",
    tech: "Pantalla digital de información del vehículo y panel de instrumentos análogos",
    application: "Transporte de larga distancia",
    extras: "Litera, aire acondicionado"
  }
}];

// ===== helpers (para la barra de 3 cuadritos) =====
const getPowerLabel = (engineText?: string) => {
  if (!engineText) return "—";
  const m = engineText.match(/(\d{2,4})\s*(HP|CV)\b/i);
  if (!m) return "—";
  const value = m[1];
  // en tu mockup se ve como "HP", así que dejamos HP aunque venga CV
  return `${value} HP`;
};
const getUsageLabel = (applicationText?: string) => {
  if (!applicationText) return "—";
  const t = applicationText.toLowerCase();
  if (t.includes("larga distancia")) return "Larga distancia";
  if (t.includes("regional")) return "Regional";
  if (t.includes("distrib")) return "Distribución";
  if (t.includes("carga general")) return "Carga general";
  if (t.includes("rescate")) return "Rescate Pesado";

  // fallback: primera parte corta
  return applicationText.split(",")[0].trim();
};
export default function FeaturedMiningEquipment() {
  const [api, setApi] = useState<CarouselApi>();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [selectedMachine, setSelectedMachine] = useState<number | null>(null);

  // Lightbox (mismo core que trucks, con thumbs vertical en desktop)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [renderedImageIndex, setRenderedImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDir, setTransitionDir] = useState<"next" | "prev">("next");

  // Refs thumbs (desktop vertical / mobile horizontal)
  const thumbsVerticalRef = useRef<HTMLDivElement | null>(null);
  const thumbsHorizontalRef = useRef<HTMLDivElement | null>(null);

  // Drag vertical/horizontal
  const isDraggingThumbsRef = useRef(false);
  const dragStartRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const dragMovedRef = useRef(false);

  // --- Swipe (mobile) para la imagen grande del lightbox ---
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchDeltaXRef = useRef(0);
  const onTouchStartImage = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartXRef.current = t.clientX;
    touchStartYRef.current = t.clientY;
    touchDeltaXRef.current = 0;
  };
  const onTouchMoveImage = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchDeltaXRef.current = t.clientX - touchStartXRef.current;
    const dy = Math.abs(t.clientY - touchStartYRef.current);
    const dx = Math.abs(touchDeltaXRef.current);
    if (dx > dy) e.preventDefault();
  };
  const onTouchEndImage = () => {
    if (isTransitioning) return;
    const dx = touchDeltaXRef.current;
    const THRESHOLD = 45;
    if (dx <= -THRESHOLD) handleNextImage();
    if (dx >= THRESHOLD) handlePrevImage();
  };
  const autoScrollRef = useRef<NodeJS.Timeout | null>(null);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const selectedMachineData = machines.find(m => m.id === selectedMachine) || null;
  useEffect(() => {
    if (!api) return;
    const startAutoScroll = () => {
      autoScrollRef.current = setInterval(() => {
        if (api.canScrollNext()) api.scrollNext();else api.scrollTo(0);
      }, 3500);
    };
    startAutoScroll();
    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    };
  }, [api]);
  useEffect(() => {
    document.body.style.overflow = selectedMachine !== null || isGalleryOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedMachine, isGalleryOpen]);

  // ESC + flechas SOLO visor
  useEffect(() => {
    if (!isGalleryOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setIsGalleryOpen(false);
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        event.stopPropagation();
        handlePrevImage();
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        event.stopPropagation();
        handleNextImage();
        return;
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGalleryOpen, selectedMachineData, isTransitioning]);

  // ESC modal (si visor abierto NO cierra modal)
  useEffect(() => {
    if (selectedMachine === null) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (isGalleryOpen) return;
      handleCloseModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedMachine, isGalleryOpen]);

  // Fade transition
  useEffect(() => {
    if (!isGalleryOpen) return;
    if (!selectedMachineData) return;
    if (renderedImageIndex === currentImageIndex) return;
    setIsTransitioning(true);
    const t = window.setTimeout(() => {
      setRenderedImageIndex(currentImageIndex);
      requestAnimationFrame(() => setIsTransitioning(false));
    }, 160);
    return () => window.clearTimeout(t);
  }, [currentImageIndex, isGalleryOpen, selectedMachineData, renderedImageIndex]);

  // Centrar thumb activo (vertical + horizontal)
  useEffect(() => {
    if (!isGalleryOpen) return;
    const verticalBtn = thumbsVerticalRef.current?.querySelector<HTMLButtonElement>(`[data-thumb="${currentImageIndex}"]`);
    verticalBtn?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest"
    });
    const horizontalBtn = thumbsHorizontalRef.current?.querySelector<HTMLButtonElement>(`[data-thumb="${currentImageIndex}"]`);
    horizontalBtn?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center"
    });
  }, [currentImageIndex, isGalleryOpen]);
  const handleMouseEnter = (id: number) => {
    setHoveredCard(id);
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  };
  const handleMouseLeave = () => {
    setHoveredCard(null);
    if (!autoScrollRef.current && api) {
      autoScrollRef.current = setInterval(() => {
        if (api.canScrollNext()) api.scrollNext();else api.scrollTo(0);
      }, 3500);
    }
  };
  const handleCardClick = (id: number) => setSelectedMachine(id);
  const handleCloseModal = () => {
    setSelectedMachine(null);
    setIsGalleryOpen(false);
  };
  const handleOpenGalleryAt = (index: number) => {
    setCurrentImageIndex(index);
    setRenderedImageIndex(index);
    setIsGalleryOpen(true);
  };
  const handleCloseGallery = () => setIsGalleryOpen(false);
  const handlePrevImage = () => {
    if (!selectedMachineData) return;
    if (isTransitioning) return;
    setTransitionDir("prev");
    setCurrentImageIndex(prev => prev === 0 ? selectedMachineData.images.length - 1 : prev - 1);
  };
  const handleNextImage = () => {
    if (!selectedMachineData) return;
    if (isTransitioning) return;
    setTransitionDir("next");
    setCurrentImageIndex(prev => prev === selectedMachineData.images.length - 1 ? 0 : prev + 1);
  };

  // Drag thumbs (vertical u horizontal)
  const onThumbsMouseDown = (e: React.MouseEvent<HTMLDivElement>, axis: "x" | "y") => {
    const container = axis === "y" ? thumbsVerticalRef.current : thumbsHorizontalRef.current;
    if (!container) return;
    isDraggingThumbsRef.current = true;
    dragMovedRef.current = false;
    dragStartRef.current = axis === "y" ? e.pageY : e.pageX;
    dragStartScrollRef.current = axis === "y" ? container.scrollTop : container.scrollLeft;
  };
  const onThumbsMouseMove = (e: React.MouseEvent<HTMLDivElement>, axis: "x" | "y") => {
    if (!isDraggingThumbsRef.current) return;
    const container = axis === "y" ? thumbsVerticalRef.current : thumbsHorizontalRef.current;
    if (!container) return;
    const delta = (axis === "y" ? e.pageY : e.pageX) - dragStartRef.current;
    if (Math.abs(delta) > 4) dragMovedRef.current = true;
    if (axis === "y") container.scrollTop = dragStartScrollRef.current - delta;else container.scrollLeft = dragStartScrollRef.current - delta;
  };
  const endThumbDrag = () => {
    isDraggingThumbsRef.current = false;
    window.setTimeout(() => {
      dragMovedRef.current = false;
    }, 0);
  };
  return <section ref={sectionRef} className="relative bg-background overflow-hidden">
      <div className="relative w-full bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1417410/img/executive_calculator_business_accounting.png)] py-20 md:py-24">
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
            <div className="shrink-0">
              <FileText className="w-24 h-24 md:w-32 md:h-32 text-foreground" />
            </div>
            <div className="text-center md:text-left">
              <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight">
                Gestionamos tu financiamiento, crédito o leasing para tu próxima
                adquisición.
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Equipos Destacados
            </h2>
            <p className="text-base md:text-lg text-muted-dark max-w-2xl mx-auto">
              Explora nuestra selección premium de camiones, máquinas y equipos
              de alta resistencia diseñadas para máxima productividad y
              confiabilidad en las condiciones mas exigentes.
            </p>
          </div>

          <div className={`relative max-w-7xl mx-auto transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <Carousel setApi={setApi} opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent className="-ml-4">
                {machines.map((machine, i) => <CarouselItem key={machine.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <Card className="group relative overflow-hidden border border-border bg-background shadow-soft hover:shadow-strong transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-2 cursor-pointer" onMouseEnter={() => handleMouseEnter(machine.id)} onMouseLeave={handleMouseLeave} onClick={() => handleCardClick(machine.id)}>
                      <div className="relative h-64 overflow-hidden bg-secondary-light">
                        <img src={machine.images[0]} alt={machine.name} className={`w-full h-full object-cover transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)] group-hover:scale-110 ${machine.id === 1 ? "object-[center_60%]" : ""} ${machine.id === 2 ? "object-[center_40%]" : ""}`} />
                        <div className={`absolute inset-0 bg-primary-dark/90 backdrop-blur-sm transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] flex flex-col items-center justify-center text-foreground-primary p-6 ${hoveredCard === machine.id ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"}`}>
                          <machine.icon className="w-12 h-12 mb-3" />
                          <p className="text-lg font-semibold text-center">
                            {machine.feature}
                          </p>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <h3 className="text-xl font-display font-bold text-foreground mb-2">
                          {machine.name}
                        </h3>
                        <p className="text-sm text-muted-dark uppercase tracking-wide">
                          {machine.category}
                        </p>
                      </CardContent>
                    </Card>
                  </CarouselItem>)}
              </CarouselContent>

              <div className="flex items-center justify-center gap-4 mt-8">
                <CarouselPrevious className="static translate-x-0 translate-y-0 bg-primary text-foreground-primary hover:bg-primary-hover border-none shadow-medium h-auto px-4 py-2">
                  <span className="ml-2 font-semibold">Anterior</span>
                </CarouselPrevious>
                <CarouselNext className="static translate-x-0 translate-y-0 bg-primary text-foreground-primary hover:bg-primary-hover border-none shadow-medium h-auto px-4 py-2">
                  <span className="mr-2 font-semibold">Siguiente</span>
                </CarouselNext>
              </div>
            </Carousel>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {selectedMachineData && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-primary-dark/90 backdrop-blur-sm animate-fade-in-up" onClick={handleCloseModal}>
          <div className="relative w-full max-w-2xl bg-background border border-border rounded-xl shadow-strong overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="relative h-64 overflow-hidden bg-secondary-light cursor-pointer group" onClick={() => handleOpenGalleryAt(0)}>
              <img src={selectedMachineData.images[0]} alt={selectedMachineData.name} className={`w-full h-full object-cover transition-transform duration-[var(--duration-medium)] group-hover:scale-105 ${selectedMachineData.id === 1 ? "object-[center_60%]" : ""} ${selectedMachineData.id === 2 ? "object-[center_40%]" : ""}`} />

              {/* BADGE (AÑO · TRACCIÓN) */}
              <div className="absolute top-4 left-4 z-10 bg-black/65 text-white text-xs font-semibold px-3 py-1 rounded-full backdrop-blur">
                {selectedMachineData.year}
                {selectedMachineData.specs?.traction ? ` · ${selectedMachineData.specs.traction}` : ""}
              </div>

              <div className="absolute inset-0 bg-primary-dark/0 group-hover:bg-primary-dark/20 transition-all duration-[var(--duration-medium)] flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-medium)] bg-primary/90 text-foreground-primary px-4 py-2 rounded-md text-sm font-semibold">
                  Click para ver todas las imagenes
                </div>
              </div>

              <Button variant="outline" size="icon" className="absolute top-4 right-4 bg-background/90 hover:bg-background border-border z-10" onClick={e => {
            e.stopPropagation();
            handleCloseModal();
          }}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto">
              <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-2">
                {selectedMachineData.name}
              </h3>

              <p className="text-accent text-lg font-semibold mb-3">
                {selectedMachineData.price}
              </p>

              <p className="text-muted-dark mb-5">
                {selectedMachineData.description}
              </p>

              {/* ===== BARRA CON 3 CUADROS ===== */}
              <div className="mb-6">
                <div className="rounded-xl bg-muted/30 border border-border overflow-hidden">
                  <div className="grid grid-cols-3 text-center">
                    <div className="px-3 py-3">
                      <p className="text-xs text-muted-dark">Potencia</p>
                      <p className="text-lg font-bold text-foreground leading-tight">
                        {getPowerLabel(selectedMachineData.specs?.engine)}
                      </p>
                    </div>

                    <div className="px-3 py-3 border-l border-border">
                      <p className="text-xs text-muted-dark">Tracción</p>
                      <p className="text-lg font-bold text-foreground leading-tight">
                        {selectedMachineData.specs?.traction ?? "—"}
                      </p>
                    </div>

                    <div className="px-3 py-3 border-l border-border">
                      <p className="text-xs text-muted-dark">Uso</p>
                      <p className="text-lg font-bold text-foreground leading-tight">
                        {getUsageLabel(selectedMachineData.specs?.application)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3">
                  <span className="font-semibold text-foreground">
                    Tipo de equipo:
                  </span>
                  <span className="text-muted-dark">
                    {selectedMachineData.specs.type}
                  </span>

                  <span className="font-semibold text-foreground">
                    Marca / Modelo:
                  </span>
                  <span className="text-muted-dark">
                    {selectedMachineData.specs.brand}
                  </span>

                  <span className="font-semibold text-foreground">Ano:</span>
                  <span className="text-muted-dark">
                    {selectedMachineData.year}
                  </span>

                  {selectedMachineData.specs.mileage && <>
                      <span className="font-semibold text-foreground">
                        Kilometraje:
                      </span>
                      <span className="text-muted-dark">
                        {selectedMachineData.specs.mileage}
                      </span>
                    </>}

                  <span className="font-semibold text-foreground">Motor:</span>
                  <span className="text-muted-dark">
                    {selectedMachineData.specs.engine}
                  </span>

                  {selectedMachineData.specs.transmission && <>
                      <span className="font-semibold text-foreground">
                        Transmision:
                      </span>
                      <span className="text-muted-dark">
                        {selectedMachineData.specs.transmission}
                      </span>
                    </>}

                  {selectedMachineData.specs.traction && <>
                      <span className="font-semibold text-foreground">
                        Traccion:
                      </span>
                      <span className="text-muted-dark">
                        {selectedMachineData.specs.traction}
                      </span>
                    </>}

                  {selectedMachineData.specs.capacity && <>
                      <span className="font-semibold text-foreground">
                        Capacidad:
                      </span>
                      <span className="text-muted-dark">
                        {selectedMachineData.specs.capacity}
                      </span>
                    </>}

                  {selectedMachineData.specs.suspension && <>
                      <span className="font-semibold text-foreground">
                        Suspension:
                      </span>
                      <span className="text-muted-dark">
                        {selectedMachineData.specs.suspension}
                      </span>
                    </>}

                  {selectedMachineData.specs.brakes && <>
                      <span className="font-semibold text-foreground">
                        Sistemas de freno y control:
                      </span>
                      <span className="text-muted-dark">
                        {selectedMachineData.specs.brakes}
                      </span>
                    </>}

                  <span className="font-semibold text-foreground">
                    Tecnologia a bordo:
                  </span>
                  <span className="text-muted-dark">
                    {selectedMachineData.specs.tech}
                  </span>

                  <span className="font-semibold text-foreground">
                    Aplicacion:
                  </span>
                  <span className="text-muted-dark">
                    {selectedMachineData.specs.application}
                  </span>

                  {selectedMachineData.specs.extras && <>
                      <span className="font-semibold text-foreground">
                        Extras:
                      </span>
                      <span className="text-muted-dark">
                        {selectedMachineData.specs.extras}
                      </span>
                    </>}
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <Button variant="outline" onClick={handleCloseModal}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* LIGHTBOX */}
      {isGalleryOpen && selectedMachineData && <div className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm" onClick={handleCloseGallery}>
          <div className="relative w-full h-full">
            {/* Top bar */}
            <div className="absolute top-4 left-4 right-4 z-30 flex items-center justify-between gap-3" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-black/45 text-white px-3 py-1.5 rounded-md text-sm font-semibold">
                  {currentImageIndex + 1} / {selectedMachineData.images.length}
                </div>
                <div className="hidden sm:block bg-black/35 text-white px-3 py-1.5 rounded-md text-sm font-medium truncate">
                  {selectedMachineData.name}
                </div>
              </div>

              <Button onClick={e => {
            e.stopPropagation();
            handleCloseGallery();
          }} variant="ghost" size="icon" className="bg-black/45 text-white hover:bg-black/65" aria-label="Cerrar galería">
                <X className="h-6 w-6" />
              </Button>
            </div>

            {/* CONTENIDO (imagen + thumbs derecha en desktop) */}
            <div className="absolute inset-0 pt-14 pb-6 px-4 sm:px-8">
              <div className="h-full w-full max-w-[1400px] mx-auto flex flex-col md:flex-row gap-4" onClick={e => e.stopPropagation()}>
                {/* Imagen principal */}
                <div className="relative flex-1 flex items-center justify-center md:pr-0 touch-pan-y" onClick={e => e.stopPropagation()} onTouchStart={onTouchStartImage} onTouchMove={onTouchMoveImage} onTouchEnd={onTouchEndImage}>
                  <Button onClick={e => {
                e.stopPropagation();
                handlePrevImage();
              }} variant="ghost" size="icon" className="absolute left-0 sm:left-2 z-20 bg-black/40 text-white hover:bg-black/60 h-12 w-12" aria-label="Imagen anterior" disabled={isTransitioning}>
                    <ChevronLeft className="h-8 w-8" />
                  </Button>

                  <img key={`${selectedMachineData.id}-${renderedImageIndex}`} src={selectedMachineData.images[renderedImageIndex]} alt={`${selectedMachineData.name} - Imagen ${renderedImageIndex + 1}`} className={["max-w-[95vw] md:max-w-[calc(100vw-220px)]", "max-h-[78vh] md:max-h-[84vh]", "object-contain rounded-xl shadow-strong select-none", "transition-all duration-300 ease-out will-change-transform will-change-opacity", isTransitioning ? transitionDir === "next" ? "opacity-0 translate-x-6 scale-[0.99]" : "opacity-0 -translate-x-6 scale-[0.99]" : "opacity-100 translate-x-0 scale-100"].join(" ")} draggable={false} />

                  <Button onClick={e => {
                e.stopPropagation();
                handleNextImage();
              }} variant="ghost" size="icon" className="absolute right-0 sm:right-2 z-20 bg-black/40 text-white hover:bg-black/60 h-12 w-12" aria-label="Imagen siguiente" disabled={isTransitioning}>
                    <ChevronRight className="h-8 w-8" />
                  </Button>
                </div>

                {/* Thumbs vertical (desktop) */}
                <div className="hidden md:block w-[160px] shrink-0">
                  <div className="h-full bg-black/35 backdrop-blur-sm rounded-xl p-3">
                    <div ref={thumbsVerticalRef} className="h-[calc(84vh-24px)] overflow-y-auto pr-1 cursor-grab active:cursor-grabbing select-none flex flex-col gap-2" onMouseDown={e => onThumbsMouseDown(e, "y")} onMouseMove={e => onThumbsMouseMove(e, "y")} onMouseUp={endThumbDrag} onMouseLeave={endThumbDrag}>
                      {selectedMachineData.images.map((src, i) => <button key={src + i} type="button" data-thumb={i} onClick={e => {
                    e.stopPropagation();
                    if (dragMovedRef.current) return;
                    if (isTransitioning) return;
                    setTransitionDir(i > currentImageIndex ? "next" : "prev");
                    setCurrentImageIndex(i);
                  }} className={["relative w-full aspect-[3/2] rounded-lg overflow-hidden border transition", i === currentImageIndex ? "border-white shadow-medium" : "border-white/15 hover:border-white/40 opacity-85 hover:opacity-100"].join(" ")} aria-label={`Ver imagen ${i + 1}`}>
                          <img src={src} alt={`Miniatura ${i + 1}`} className="h-full w-full object-cover pointer-events-none" draggable={false} loading="lazy" />
                        </button>)}
                    </div>

                    <p className="mt-2 text-center text-[11px] text-white/50">
                      Tip: usa ← → para cambiar
                    </p>
                  </div>
                </div>

                {/* Thumbs horizontal (mobile) */}
                <div className="md:hidden">
                  <div className="bg-black/35 backdrop-blur-sm rounded-xl p-3">
                    <div ref={thumbsHorizontalRef} className="flex gap-2 overflow-x-auto cursor-grab active:cursor-grabbing select-none" onMouseDown={e => onThumbsMouseDown(e, "x")} onMouseMove={e => onThumbsMouseMove(e, "x")} onMouseUp={endThumbDrag} onMouseLeave={endThumbDrag}>
                      {selectedMachineData.images.map((src, i) => <button key={src + i} type="button" data-thumb={i} onClick={e => {
                    e.stopPropagation();
                    if (dragMovedRef.current) return;
                    if (isTransitioning) return;
                    setTransitionDir(i > currentImageIndex ? "next" : "prev");
                    setCurrentImageIndex(i);
                  }} className={["relative shrink-0 h-16 w-24 rounded-lg overflow-hidden border transition", i === currentImageIndex ? "border-white shadow-medium" : "border-white/15 hover:border-white/40 opacity-85 hover:opacity-100"].join(" ")} aria-label={`Ver imagen ${i + 1}`}>
                          <img src={src} alt={`Miniatura ${i + 1}`} className="h-full w-full object-cover pointer-events-none" draggable={false} loading="lazy" />
                        </button>)}
                    </div>

                    <p className="mt-2 text-center text-[11px] text-white/50">
                      Tip: usa ← → para cambiar
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* FIN CONTENIDO */}
          </div>
        </div>}
    </section>;
}